import React, { useEffect, useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import desktop from "../../assets/services/desktop.png";
import ecom from "../../assets/services/ecom.png";
import mobile from "../../assets/services/mobile.png";
import webDesign from "../../assets/services/webdesign.png";
import webDev from "../../assets/services/webdev.png";
import backend from "../../assets/services/backend.png";
import PageHeader from "components/Header/PageHeader";
import { Loader } from "lucide-react";
import { Link } from "react-router-dom";

const services = [
  {
    id: 1,
    title: "Web Development",
    description:
      "Transform your business vision into a powerful digital presence that works 24/7 to generate leads and drive sales.",
    features: [
      "Custom responsive websites",
      "Performance optimization",
      "SEO-friendly architecture",
      "Security implementation",
    ],
    roi: "Increase online visibility by 300% and generate qualified leads round the clock",
    image: webDev,
    price: "Starting from $1,500",
    investment:
      "Your website becomes your most valuable sales asset, working continuously to bring customers to your door.",
  },
  {
    id: 5,
    title: "Mobile Applications",
    description:
      "Reach your customers wherever they are with native mobile apps that enhance engagement and drive loyalty.",
    features: [
      "Android development",
      "Push notifications",
      "Offline functionality",
      "App store optimization",
    ],
    roi: "Increase customer engagement by 400% and boost repeat purchases by 55%",
    image: mobile,
    price: "Starting from $4,000",
    investment:
      "Put your business in your customers' pockets, creating constant touchpoints for increased sales.",
  },
  {
    id: 6,
    title: "Desktop Applications",
    description:
      "Boost productivity with custom desktop solutions that automate workflows and eliminate manual processes.",
    features: [
      "Cross-platform compatibility",
      "Database integration",
      "Workflow automation",
      "User management systems",
    ],
    roi: "Reduce operational costs by 50% and increase team productivity by 70%",
    image: desktop,
    price: "Starting from $3,500",
    investment:
      "Eliminate repetitive tasks and free your team to focus on revenue-generating activities.",
  },
  {
    id: 8,
    title: "Backend Development",
    description:
      "Build robust, scalable backend systems that handle growth and ensure your applications perform flawlessly.",
    features: [
      "API development",
      "Database optimization",
      "Cloud infrastructure",
      "Security protocols",
    ],
    roi: "Reduce system downtime by 95% and support 10x user growth without performance loss",
    image: backend,
    price: "Starting from $2,200",
    investment:
      "Solid infrastructure that scales with your success, protecting your growing business investment.",
  },

  {
    id: 7,
    title: "E-Commerce Sites",
    description:
      "Launch your online store with conversion-optimized e-commerce platforms that turn visitors into buyers.",
    features: [
      "Payment gateway integration",
      "Inventory management",
      "Order tracking",
      "Marketing automation",
    ],
    roi: "Generate 24/7 sales with average revenue increase of 200% within first year",
    image: ecom,
    price: "Starting from $2,800",
    investment:
      "Every feature is designed to maximize sales and create recurring revenue streams.",
  },
  {
    id: 2,
    title: "Web Designing",
    description:
      "Create stunning, user-centric designs that captivate visitors and convert them into loyal customers.",
    features: [
      "Modern UI/UX design",
      "Brand identity integration",
      "Mobile-first approach",
      "Conversion optimization",
    ],
    roi: "Boost user engagement by 250% and increase conversion rates by up to 40%",
    image: webDesign,
    price: "Starting from $800",
    investment:
      "Every design element works strategically to guide visitors toward taking action and becoming customers.",
  },
  {
    id: 3,
    title: "CRM Development",
    description:
      "Streamline your customer relationships with custom CRM solutions that automate processes and maximize revenue per customer.",
    features: [
      "Customer lifecycle management",
      "Sales pipeline automation",
      "Analytics & reporting",
      "Integration capabilities",
    ],
    roi: "Increase sales productivity by 45% and improve customer retention by 35%",
    image: webDev,
    price: "Starting from $3,000",
    investment:
      "Transform scattered customer data into actionable insights that drive consistent revenue growth.",
  },
  {
    id: 4,
    title: "Custom Full Stack Dashboards",
    description:
      "Make data-driven decisions with powerful dashboards that provide real-time insights and actionable intelligence.",
    features: [
      "Real-time data visualization",
      "Custom analytics",
      "Multi-platform compatibility",
      "Advanced filtering",
    ],
    roi: "Reduce decision-making time by 60% and identify new revenue opportunities 3x faster",
    image: webDev,
    price: "Starting from $2,500",
    investment:
      "Transform complex data into clear strategies that drive measurable business growth.",
  },
];

export default function Services() {
  const [isLoading, setIsLoading] = useState(true);

  // Helper function to create the Google Calendar URL
  const createGoogleMeetLink = (serviceTitle) => {
    const title = encodeURIComponent(`30 Min Free Demo: ${serviceTitle}`);
    const details = encodeURIComponent(
      `Free 30-minute consultation for a ${serviceTitle} service. 
    
    A Google Meet link will be automatically generated by Google Calendar. 
    Please select a time slot and save the event to notify me of your booking.`
    );
    const guestEmail = "mnoumankhalid195@gmail.com";
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&details=${details}&add=${guestEmail}&tmeid=0&sf=true&output=xml`;
  };

  const handleScheduleMeet = (serviceTitle) => {
    const calendarUrl = createGoogleMeetLink(serviceTitle);
    window.open(calendarUrl, "_blank");
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return (
      <div className="loader-container">
        <Loader className="spinner" size={48} />
        <style jsx>{`
          .loader-container {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100vh;
            background: #0d1117;
          }
          .spinner {
            animation: spin 1s linear infinite;
            color: #ff0080;
          }
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </div>
    );
  }

  return (
    <HelmetProvider>
      <div
        style={{
          backgroundColor: "#0a0e27",
          minHeight: "100vh",
          color: "#ffffff",
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        }}
      >
        <Helmet>
          <meta charSet="utf-8" />
          <title>Services | Professional Development Solutions</title>
          <meta
            name="description"
            content="Transform your business with our comprehensive development services. From web development to mobile apps, we deliver solutions that drive ROI."
          />
        </Helmet>

        <PageHeader
          title="My Services"
          breadcrumb={[
            { label: "Home", path: "/" },
            { label: "Services", path: "/services" },
          ]}
          backgroundImage="https://stackify.com/wp-content/uploads/2017/09/Retrace-Web-Services-881x441-1.jpg"
        />

        {/* Services Grid */}
        <div
          style={{ padding: "60px 20px", maxWidth: "1400px", margin: "0 auto" }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
              gap: "2rem",
              marginBottom: "3rem",
              maxWidth: "1200px",
              margin: "0 auto 3rem auto",
            }}
            className="services-grid"
          >
            {services.map((service) => (
              <div
                key={service.id}
                style={{
                  background: "linear-gradient(145deg, #131829, #1a1f3a)",
                  borderRadius: "16px",
                  padding: "0",
                  border: "1px solid #2a2f4a",
                  boxShadow: "0 10px 25px rgba(0, 0, 0, 0.3)",
                  transition: "all 0.3s ease",
                  overflow: "hidden",
                  cursor: "pointer",
                  height: "fit-content",
                  maxWidth: "380px",
                  margin: "0 auto",
                }}
                className="service-card"
              >
                {/* Service Image */}
                <div
                  style={{
                    height: "250px",
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: "16px 16px 0 0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={service.image}
                    alt={service.title}
                    style={{
                      height: "100%",
                      width: "100%",
                      objectFit: "contain",
                      objectPosition: "center",
                      transition: "transform 0.3s ease",
                      display: "block",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.05)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  />
                </div>

                {/* Service Content */}
                <div style={{ padding: "1.5rem" }}>
                  <h3
                    style={{
                      fontSize: "1.3rem",
                      fontWeight: "700",
                      marginBottom: "0.8rem",
                      color: "#ff69b4",
                      textShadow: "0 0 10px rgba(255, 105, 180, 0.3)",
                    }}
                  >
                    {service.title}
                  </h3>

                  <p
                    style={{
                      color: "#e0e6ff",
                      marginBottom: "1.2rem",
                      lineHeight: "1.5",
                      fontSize: "0.9rem",
                      display: "-webkit-box",
                      WebkitLineClamp: "3",
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {service.description}
                  </p>

                  {/* Features */}
                  <div style={{ marginBottom: "1.2rem" }}>
                    <h4
                      style={{
                        color: "#ffffff",
                        fontSize: "0.8rem",
                        fontWeight: "600",
                        marginBottom: "0.6rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Key Features:
                    </h4>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.4rem",
                      }}
                    >
                      {service.features.slice(0, 3).map((feature, index) => (
                        <div
                          key={index}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            color: "#b0b8d4",
                            fontSize: "0.8rem",
                          }}
                        >
                          <div
                            style={{
                              width: "6px",
                              height: "6px",
                              background:
                                "linear-gradient(45deg, #ff0080, #ff69b4)",
                              borderRadius: "50%",
                              marginRight: "8px",
                              boxShadow: "0 0 6px rgba(255, 0, 128, 0.5)",
                            }}
                          ></div>
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* ROI Section - Simplified */}
                  <div
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(255, 0, 128, 0.1), rgba(255, 105, 180, 0.05))",
                      borderRadius: "12px",
                      padding: "1rem",
                      marginBottom: "1.2rem",
                      border: "1px solid rgba(255, 0, 128, 0.2)",
                    }}
                  >
                    <p
                      style={{
                        color: "#ffffff",
                        fontSize: "0.85rem",
                        fontWeight: "500",
                        margin: "0",
                      }}
                    >
                      📈 {service.roi}
                    </p>
                  </div>

                  {/* Action Button */}
                  <button
                    onClick={() => handleScheduleMeet(service.title)}
                    style={{
                      width: "100%",
                      background: "linear-gradient(135deg, #ff0080, #ff1493)",
                      color: "white",
                      border: "none",
                      padding: "12px 20px",
                      borderRadius: "10px",
                      fontSize: "0.95rem",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      boxShadow: "0 6px 15px rgba(255, 0, 128, 0.3)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.02)";
                      e.currentTarget.style.boxShadow =
                        "0 8px 20px rgba(255, 0, 128, 0.4)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                      e.currentTarget.style.boxShadow =
                        "0 6px 15px rgba(255, 0, 128, 0.3)";
                    }}
                  >
                    Schedule Free Meeting →
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Call to Action Section */}
          <div
            style={{
              textAlign: "center",
              padding: "3rem 2rem",
              background: "linear-gradient(135deg, #131829, #1a1f3a)",
              borderRadius: "16px",
              border: "1px solid #2a2f4a",
            }}
          >
            <h2
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                marginBottom: "1rem",
                background: "linear-gradient(135deg, #ff0080, #ff69b4)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
              className="cta-title"
            >
              Ready to Transform Your Business?
            </h2>
            <p
              style={{
                color: "#e0e6ff",
                fontSize: "1.1rem",
                marginBottom: "2rem",
                maxWidth: "600px",
                margin: "0 auto 2rem",
              }}
              className="cta-text"
            >
              Don't just spend on development – invest in solutions that deliver
              measurable returns.
            </p>
            <Link
              to={"/contact"}
              style={{
                background: "linear-gradient(135deg, #ff0080, #ff1493)",
                color: "white",
                textDecoration: "none",
                border: "none",
                padding: "12px 30px",
                borderRadius: "20px",
                fontSize: "1rem",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.3s ease",
                boxShadow: "0 8px 20px rgba(255, 0, 128, 0.3)",
              }}
              className="cta-button"
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.05)";
                e.currentTarget.style.boxShadow =
                  "0 12px 30px rgba(255, 0, 128, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow =
                  "0 8px 20px rgba(255, 0, 128, 0.3)";
              }}
            >
              Get Started Today
            </Link>
          </div>
        </div>

        {/* Responsive CSS */}
        <style jsx>{`
          @media (max-width: 1024px) {
            .services-grid {
              grid-template-columns: repeat(
                auto-fit,
                minmax(320px, 1fr)
              ) !important;
              gap: 1.5rem !important;
            }
          }

          @media (max-width: 768px) {
            .services-grid {
              grid-template-columns: 1fr !important;
              gap: 1.5rem !important;
              max-width: 400px !important;
            }

            .service-card {
              margin: 0 auto !important;
              max-width: 350px !important;
            }

            .cta-title {
              font-size: 1.6rem !important;
            }

            .cta-text {
              font-size: 1rem !important;
              padding: 0 1rem;
            }

            .cta-button {
              padding: 10px 25px !important;
              font-size: 0.9rem !important;
            }
          }

          @media (max-width: 480px) {
            .services-grid {
              padding: 0 10px;
            }

            .service-card > div:last-child {
              padding: 1.2rem !important;
            }

            .service-card h3 {
              font-size: 1.1rem !important;
            }

            .service-card p {
              font-size: 0.85rem !important;
            }

            .cta-title {
              font-size: 1.4rem !important;
            }
          }
        `}</style>
      </div>
    </HelmetProvider>
  );
}
